import React, { useMemo, useState } from 'react';
import { DEVICE_TYPES, DEVICE_CATEGORIES, deviceLabel, deviceCategoryLabel } from '@/lib/deviceIcons';
import { CONNECTION_TYPES, connLabel } from '@/lib/connections';
import { DeviceIcon } from '@/components/DeviceIcon';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useI18n } from '@/i18n/I18nContext';
import { useStore, getActivePage } from '@/state/store';
import { pageDisplayName } from '@/lib/pageNames';
import { standardPages } from '@/lib/detail';
import { Search, Boxes, Copy, Plus, Check } from 'lucide-react';

// Bottom connection legend — shared by both palette modes.
const ConnectionLegend = () => {
  const { t, language } = useI18n();
  const activeConnType = useStore((s) => s.activeConnType);
  const setActiveConnType = useStore((s) => s.setActiveConnType);
  return (
    <div className="border-t border-border px-3 py-2.5" data-testid="connection-legend">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('connections')}</span>
      </div>
      <div className="space-y-1">
        {CONNECTION_TYPES.map((c) => {
          const active = c.key === activeConnType;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveConnType(c.key)}
              data-testid={`conn-type-${c.key}`}
              className={`flex w-full items-center gap-2.5 rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${
                active ? 'border-[#0B5FFF]/40 bg-white shadow-sm' : 'border-transparent hover:bg-white/70'
              }`}
            >
              <svg width="34" height="10" className="shrink-0">
                <line x1="1" y1="5" x2="33" y2="5" stroke={c.color} strokeWidth={c.width} strokeDasharray={c.dash || undefined} />
              </svg>
              <span className={`truncate ${active ? 'font-semibold text-[#111418]' : 'text-[#2A3138]'}`}>
                {connLabel(c.key, language)}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">{t('active_conn_hint')}</p>
    </div>
  );
};

// "New devices" mode — the device-type catalogue, grouped by category.
const NewDevices = () => {
  const { t, language } = useI18n();
  const [q, setQ] = useState('');
  const addDevice = useStore((s) => s.addDevice);
  const page = useStore(getActivePage);

  const groups = DEVICE_CATEGORIES.map((cat) => ({
    cat,
    items: DEVICE_TYPES.filter(
      (d) => d.cat === cat.key && deviceLabel(d.key, language).toLowerCase().includes(q.toLowerCase()),
    ),
  })).filter((g) => g.items.length > 0);

  const onDragStart = (e, key) => {
    e.dataTransfer.setData('application/netdraw-device', key);
    e.dataTransfer.effectAllowed = 'copy';
  };
  const onClickAdd = (key) => {
    if (!page) return;
    addDevice(key, { x: 120 + Math.round(Math.random() * 220), y: 120 + Math.round(Math.random() * 180) });
  };

  return (
    <>
      <div className="px-3 py-2.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="device-palette-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('search_devices')}
            className="h-9 pl-8 text-sm"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-2 pb-3">
        {groups.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-muted-foreground">{t('search_no_results')}</p>
        ) : (
          groups.map((g) => (
            <div key={g.cat.key} className="mb-3" data-testid={`palette-cat-${g.cat.key}`}>
              <div className="px-1 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {deviceCategoryLabel(g.cat.key, language)}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {g.items.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    draggable
                    onDragStart={(e) => onDragStart(e, d.key)}
                    onClick={() => onClickAdd(d.key)}
                    title={t('drag_hint')}
                    data-testid={`palette-item-${d.key}`}
                    className="group flex cursor-grab flex-col items-center gap-1 rounded-md border border-transparent bg-white px-2 py-3 text-[#1b232c] ring-1 ring-border transition-all duration-150 hover:-translate-y-0.5 hover:border-[#0B5FFF]/40 hover:shadow-sm active:cursor-grabbing"
                  >
                    <DeviceIcon type={d.key} size={34} />
                    <span className="text-center text-[11px] font-medium leading-tight text-[#2A3138]">
                      {deviceLabel(d.key, language)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

// "Existing devices" mode — every master device across the standard pages.
// Clicking an available device places it on the current page as a CLONE (alias);
// devices already present on this page are greyed out and just get selected.
const ExistingDevices = () => {
  const { t, language } = useI18n();
  const [q, setQ] = useState('');
  const doc = useStore((s) => s.doc);
  const page = useStore(getActivePage);
  const addDeviceAlias = useStore((s) => s.addDeviceAlias);
  const selectNode = useStore((s) => s.selectNode);
  const selectedNodeId = useStore((s) => s.selectedNodeId);

  // Master device id is "present" on the active page if its master node lives
  // here OR a clone (alias) of it is placed here.
  const presentIds = useMemo(() => {
    const set = new Set();
    (page?.nodes || []).forEach((n) => set.add(n.data?.aliasOf || n.id));
    return set;
  }, [page]);

  const devices = useMemo(() => {
    const list = [];
    for (const p of standardPages(doc)) {
      for (const n of p.nodes || []) {
        if (n.data?.aliasOf) continue; // master devices only
        list.push({
          id: n.id,
          name: n.data?.label || deviceLabel(n.data?.deviceType, language),
          deviceType: n.data?.deviceType,
          page: pageDisplayName(p.name, language),
        });
      }
    }
    return list;
  }, [doc, language]);

  const filtered = useMemo(
    () => devices.filter((d) => d.name.toLowerCase().includes(q.trim().toLowerCase())),
    [devices, q]
  );

  const onItemClick = (d) => {
    if (!page) return;
    if (presentIds.has(d.id)) selectNode(d.id);
    else addDeviceAlias(d.id);
  };

  return (
    <>
      <div className="px-3 pt-2.5">
        <p className="mb-2 text-[11px] leading-snug text-muted-foreground">{t('existing_devices_hint')}</p>
        {devices.length > 0 && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="existing-device-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('search_devices')}
              className="h-9 pl-8 text-sm"
            />
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-2 pb-3 pt-2">
        {devices.length === 0 ? (
          <div
            className="mx-1 mt-2 rounded-md border border-dashed border-border bg-white/60 px-3 py-4 text-center"
            data-testid="existing-devices-empty"
          >
            <p className="text-[11px] leading-snug text-muted-foreground">{t('existing_empty')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {filtered.map((d) => {
              const added = presentIds.has(d.id);
              const selected = selectedNodeId === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onItemClick(d)}
                  data-testid={`existing-device-item-${d.id}`}
                  data-added={added ? 'true' : 'false'}
                  title={added ? t('existing_already_on_page') : t('existing_add_to_page')}
                  className={`group flex w-full items-center gap-2.5 rounded-md border px-2 py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B5FFF]/40 ${
                    added
                      ? 'border-transparent bg-[#1b232c]/[0.05] hover:bg-[#1b232c]/[0.08]'
                      : selected
                      ? 'border-[#0B5FFF]/40 bg-white shadow-sm'
                      : 'border-border bg-white hover:border-[#0B5FFF]/40 hover:shadow-sm'
                  }`}
                >
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center ${added ? 'opacity-50' : ''}`}>
                    <DeviceIcon type={d.deviceType} size={28} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`truncate text-[12.5px] font-medium ${added ? 'text-muted-foreground' : 'text-[#111418]'}`}>
                      {d.name}
                    </div>
                    <div className="truncate text-[10.5px] text-muted-foreground">{d.page}</div>
                  </div>
                  {added ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#6B4FBB]/10 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-[#6B4FBB]">
                      <Check className="h-3 w-3" /> {t('detail_added_badge')}
                    </span>
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-2 py-3 text-[11px] text-muted-foreground" data-testid="existing-devices-no-match">
                {t('no_devices')}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export const DevicePalette = () => {
  const { t } = useI18n();
  const [mode, setMode] = useState('new');

  return (
    <div className="flex h-full flex-col bg-[#FBFBFA]" data-testid="device-palette">
      <div className="border-b border-border px-3 py-2.5">
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(v) => v && setMode(v)}
          className="grid w-full grid-cols-2 gap-1"
          data-testid="palette-mode-toggle"
        >
          <ToggleGroupItem value="new" className="h-8 gap-1.5 text-xs" data-testid="palette-mode-new">
            <Boxes className="h-3.5 w-3.5" /> {t('devices')}
          </ToggleGroupItem>
          <ToggleGroupItem value="existing" className="h-8 gap-1.5 text-xs" data-testid="palette-mode-existing">
            <Copy className="h-3.5 w-3.5" /> {t('palette_existing_devices')}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {mode === 'new' ? <NewDevices /> : <ExistingDevices />}

      <ConnectionLegend />
    </div>
  );
};
